import { NotFoundPage } from '@/pages/NotFound/NotFoundPage';
import { RootRedirect } from '@/pages/RootRedirect';
import { SignInPage } from '@/pages/SignIn/SignInPage';

import {
  CheckoutPage,
  CliAuthPage,
  CreateWorkspacePage,
  InviteAcceptPage,
  OAuthConsentPage,
  ScanPage,
  SharedReportPage,
  SharedSavedReportPage,
  UnsubscribePage,
} from './lazy-pages';
import { inShellRouteEntries } from './route-table.in-shell';

import type {
  OutOfShellRouteEntry,
  PageOrRedirectRouteEntry,
  RouteEntry,
} from './route-table.types';

/**
 * The dashboard route table — the single source of truth for every path the SPA
 * router serves. `routes.tsx` and `out-of-shell-routes.tsx` render FROM this
 * table, so the router and the table can never drift (a `route-table.test.tsx`
 * drift guard asserts the rendered `<Route>` set equals this table exactly).
 *
 * Lazy page chunks stay declared in `./lazy-pages` (A23 lazy chunking); this
 * table only references them, so no page is eagerly pulled into the entry bundle.
 * The typed entry shapes (the three `kind`s — `page` / `redirect` / `out-of-shell`
 * — and `placement`, which is load-bearing: an in-shell redirect sits behind the
 * AuthGuard) live in `./route-table.types`, re-exported below so consumers import
 * both the data and the types from `./route-table`.
 */

export type {
  OutOfShellRouteEntry,
  PageOrRedirectRouteEntry,
  PageRouteEntry,
  RedirectRouteEntry,
  RouteComponent,
  RouteEntry,
  RouteKind,
  RoutePlacement,
} from './route-table.types';

/**
 * Top-level routes — rendered directly in `<Routes>`, outside the AppShell layout.
 * The front door (`/` redirect + `/signin`), the `/app` legacy alias, and the
 * `*` catch-all 404. React Router v6 ranks routes independently of declaration
 * order, so the `*` catch-all always loses to a more specific match.
 */
export const topLevelRouteEntries: readonly PageOrRedirectRouteEntry[] = [
  { kind: 'page', placement: 'top', path: '/', component: RootRedirect },
  { kind: 'page', placement: 'top', path: '/signin', component: SignInPage },
  { kind: 'redirect', placement: 'top', path: '/app', to: '/home' },
  { kind: 'page', placement: 'top', path: '*', component: NotFoundPage },
];

/**
 * Out-of-shell routes — rendered outside the AuthGuard + AppShell layout, each
 * behind its own Suspense boundary. Two families: public (unauthenticated,
 * token/URL-driven) and authenticated interstitials (`auth: true`, behind
 * AuthGuard but without the workspace chrome).
 */
export { inShellRouteEntries };

export const outOfShellRouteEntries: readonly OutOfShellRouteEntry[] = [
  // A24/AF-133 — the public no-signup scan preview (marketing links here).
  { kind: 'out-of-shell', path: '/scan', component: ScanPage, auth: false },
  // C34(3) — public OAuth consent interstitial, driven by the ?consent_token.
  { kind: 'out-of-shell', path: '/oauth/consent', component: OAuthConsentPage, auth: false },
  // C-12 — the public shared Agent-Impact report (`:token` is the capability).
  {
    kind: 'out-of-shell',
    path: '/shared/impact/:token',
    component: SharedReportPage,
    auth: false,
  },
  // REP-1b — the public shared saved report. A SEPARATE path from the impact one
  // above rather than a mode of it: the two carry different token prefixes (`srp_`
  // vs `shr_`), so each endpoint's shape gate refuses the other's token before
  // touching a database, and a forwarded link that lands on the wrong page would
  // otherwise read as a broken link rather than an unknown one.
  {
    kind: 'out-of-shell',
    path: '/shared/report/:token',
    component: SharedSavedReportPage,
    auth: false,
  },
  // C-12 — the public no-login unsubscribe confirmation for the weekly report email
  // (four segments vs three, so it never collides with the report path above).
  {
    kind: 'out-of-shell',
    path: '/shared/impact/unsubscribe/:token',
    component: UnsubscribePage,
    auth: false,
  },
  // The audit-digest unsubscribe — the SAME page in its audit-digest mode. A
  // digest recipient is someone a workspace owner added and may have no
  // Syncanix account at all, so it stays public/no-login like the impact one.
  {
    kind: 'out-of-shell',
    path: '/audit/digest/unsubscribe/:token',
    component: UnsubscribePage,
    auth: false,
    props: { variant: 'audit-digest' },
  },
  // DE-206 — the documentation-digest unsubscribe, the SAME page in its third
  // mode. Public/no-login for the same reason: a digest recipient is someone a
  // workspace owner added and may have no Syncanix account at all.
  {
    kind: 'out-of-shell',
    path: '/docs/digest/unsubscribe/:token',
    component: UnsubscribePage,
    auth: false,
    props: { variant: 'docs-digest' },
  },
  {
    kind: 'out-of-shell',
    path: '/onboarding/create-workspace',
    component: CreateWorkspacePage,
    auth: true,
  },
  { kind: 'out-of-shell', path: '/cli-auth', component: CliAuthPage, auth: true },
  // ADR-0118 — the Paddle "default payment link" target; a transient checkout
  // interstitial, authenticated but outside the AppShell.
  { kind: 'out-of-shell', path: '/checkout', component: CheckoutPage, auth: true },
  // Invite-email landing (B11/OQ-3) — authenticated, outside the AppShell: the
  // accepter holds no membership in the inviting workspace yet.
  { kind: 'out-of-shell', path: '/invite/accept', component: InviteAcceptPage, auth: true },
];

/** Every route the router serves, in render order (top-level → out-of-shell → in-shell). */
export const allRouteEntries: readonly RouteEntry[] = [
  ...topLevelRouteEntries,
  ...outOfShellRouteEntries,
  ...inShellRouteEntries,
];
